/**
 * Petstyle24 – Produktdaten-Baustein, Lese-Seite im Browser (Gesamtplan Et. 7; Build-Plan v2 §4.4; UI-Vertrag §3 Nr. 3).
 *
 * Liest den Datensatz, den snippets/ps-product-data-json.liquid serverseitig schreibt
 * (<script type="application/json" data-ps-product-data>). Nutzer: Vergleich (Et. 8) und Quiz (Et. 11).
 * Die Fakten kommen nur vom Server: Hier wird nichts nachgeladen, nichts gespeichert und nichts ins DOM geschrieben.
 *
 * Petstyle24.productData.read(scope) → { ok: true, products } | { ok: false, reason }
 *   scope: Element oder Document mit genau einem Datensatz.
 *   Streng wie der UI-Vertrag (die UI erfindet nie etwas): ungültiges JSON, ein fehlender oder unbekannter Schlüssel,
 *   ein falscher Typ oder eine doppelte ID → ok: false. Die Nutzer zeigen dann „nicht eingerichtet“ bzw.
 *   „noch nicht hinterlegt“.
 * Petstyle24.productData.state(product, field) → 'value' | 'empty' | 'not_configured'
 *   Alle Fakten außer lifecycle_status gelten nur bei eingerichteten Produkten (configured). lifecycle_status steht
 *   immer im Datensatz – wie das Restbestand-Abzeichen der Produktseite.
 * Petstyle24.productData.byId(products, id) → Produkt oder null
 *
 * Produkt (unveränderlich, Schlüssel wie im Datensatz):
 *   { id, configured, lifecycle_status, recommendation_status, construction_type, function_modules, care_goals,
 *     coat_profiles, colors, variants: [{ id, available, width }] } – width 4.5 | 9 | null.
 *   Nicht eingerichtete Produkte haben nur id, configured, lifecycle_status und variants.
 * Keine Preise, keine Bestandsmengen, keine Titel, Handles, Modellcodes oder SKUs (D-006, 05b §19).
 */
(() => {
  'use strict';

  const PS = window.Petstyle24;
  if (!PS || PS.productData) return;

  const SELECTOR = 'script[type="application/json"][data-ps-product-data]';
  const BASE_KEYS = ['configured', 'id', 'lifecycle_status', 'variants'];
  const FACT_KEYS = ['care_goals', 'coat_profiles', 'colors', 'construction_type', 'function_modules', 'recommendation_status'];
  const LIST_KEYS = ['function_modules', 'care_goals', 'coat_profiles', 'colors']; // Reihenfolge wie im Datensatz
  const VARIANT_KEYS = ['available', 'id', 'width'];
  const CONFIGURED_KEYS = BASE_KEYS.concat(FACT_KEYS).sort().join();
  const PLAIN_KEYS = BASE_KEYS.join();
  const CODE_RE = /^[a-z_]{1,40}$/;
  const COLOR_RE = /^[A-ZÄÖÜ][a-zäöüß]{1,19}$/;
  const WIDTHS = [4.5, 9];

  const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
  const isId = (value) => Number.isSafeInteger(value) && value > 0;
  const isCode = (value) => typeof value === 'string' && CODE_RE.test(value);
  const keysOf = (value) => Object.keys(value).sort().join();
  const fail = (reason) => Object.freeze({ ok: false, reason });

  const list = (value, pattern) => {
    if (!Array.isArray(value)) return null;
    if (!value.every((item) => typeof item === 'string' && pattern.test(item))) return null;
    if (new Set(value).size !== value.length) return null;
    return Object.freeze(value.slice());
  };

  const variants = (value, seen) => {
    if (!Array.isArray(value) || value.length === 0) return null;
    const out = [];
    for (const entry of value) {
      if (!isObject(entry) || keysOf(entry) !== VARIANT_KEYS.join()) return null;
      if (!isId(entry.id) || seen.has(entry.id) || typeof entry.available !== 'boolean') return null;
      if (entry.width !== null && !WIDTHS.includes(entry.width)) return null;
      seen.add(entry.id);
      out.push(Object.freeze({ id: entry.id, available: entry.available, width: entry.width }));
    }
    return Object.freeze(out);
  };

  const normalize = (entry, products, variantIds) => {
    if (!isObject(entry) || typeof entry.configured !== 'boolean') return null;
    if (keysOf(entry) !== (entry.configured ? CONFIGURED_KEYS : PLAIN_KEYS)) return null;
    if (!isId(entry.id) || products.has(entry.id)) return null;
    if (entry.lifecycle_status !== null && !isCode(entry.lifecycle_status)) return null;
    const out = { id: entry.id, configured: entry.configured, lifecycle_status: entry.lifecycle_status };
    if (entry.configured) {
      if (!isCode(entry.recommendation_status)) return null;
      if (entry.construction_type !== null && !isCode(entry.construction_type)) return null;
      out.recommendation_status = entry.recommendation_status;
      out.construction_type = entry.construction_type;
      for (const key of LIST_KEYS) {
        out[key] = list(entry[key], key === 'colors' ? COLOR_RE : CODE_RE);
        if (!out[key]) return null;
      }
    }
    out.variants = variants(entry.variants, variantIds);
    if (!out.variants) return null;
    products.add(entry.id);
    return Object.freeze(out);
  };

  const read = (scope) => {
    if (!scope || typeof scope.querySelectorAll !== 'function') return fail('scope');
    const blocks = scope.querySelectorAll(SELECTOR);
    if (blocks.length !== 1) return fail(blocks.length ? 'multiple' : 'missing');
    let raw = null;
    try {
      raw = JSON.parse(blocks[0].textContent || '');
    } catch (error) {
      return fail('json');
    }
    if (!Array.isArray(raw)) return fail('shape');
    const productIds = new Set();
    const variantIds = new Set();
    const products = [];
    for (const entry of raw) {
      const product = normalize(entry, productIds, variantIds);
      if (!product) return fail('entry');
      products.push(product);
    }
    return Object.freeze({ ok: true, products: Object.freeze(products) });
  };

  const state = (product, field) => {
    if (!isObject(product) || !(field === 'lifecycle_status' || FACT_KEYS.includes(field))) return 'not_configured';
    if (field !== 'lifecycle_status' && product.configured !== true) return 'not_configured';
    const value = product[field];
    if (Array.isArray(value)) return value.length ? 'value' : 'empty';
    return value ? 'value' : 'empty';
  };

  const byId = (products, id) => (Array.isArray(products) ? products.find((product) => product.id === Number(id)) || null : null);

  PS.productData = Object.freeze({ read, state, byId });
})();
